/**
 * Session-expiry bridge.
 *
 * Module-level code (axios response interceptors, the Keycloak `onTokenExpired`
 * callback) cannot render React UI directly. Previously those paths called
 * `logout()` the moment a token refresh failed, dropping the user to the Keycloak
 * login redirect with no warning — losing any in-progress edit silently.
 *
 * Instead they now call `notifySessionExpired()`, and a mounted React component
 * (`SessionExpiredDialog`) registers a handler that shows a re-login prompt. A
 * single subscriber is sufficient (one dialog); registering again replaces it.
 */
type SessionExpiredHandler = () => void;

let handler: SessionExpiredHandler | null = null;
let sessionExpired = false;

/** Register (or clear, with `null`) the React handler that shows the prompt. */
export const setSessionExpiredHandler = (next: SessionExpiredHandler | null): void => {
  handler = next;
  if (handler && sessionExpired) {
    handler();
  }
};

/** Clear the latched expiry signal after the user explicitly chooses a next step. */
export const clearSessionExpiredNotification = (): void => {
  sessionExpired = false;
};

/**
 * Signal that the session has expired. Safe to call from non-React modules and
 * safe to call repeatedly — the handler guards against re-opening.
 */
export const notifySessionExpired = (): void => {
  sessionExpired = true;
  handler?.();
};
